import { useRef, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import { allTools, Tool } from '../../mocks/tools';
import { useCompare } from '../../context/CompareContext';
import { useSEO } from '../../hooks/useSEO';
import ToolPicker from './components/ToolPicker';
import ComparisonSection from './components/ComparisonSection';

export default function ComparePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { compareSlugs, toggleTool, isInCompare, count } = useCompare();
  const comparisonRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://toolnova.ai';

  // Sync URL params → context on first mount
  useEffect(() => {
    const urlSlugs = (searchParams.get('tools') || '').split(',').filter(Boolean);
    urlSlugs.forEach(slug => {
      if (!isInCompare(slug) && allTools.find(t => t.slug === slug)) {
        toggleTool(slug);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep URL in sync with context
  useEffect(() => {
    if (compareSlugs.length > 0) {
      navigate(`/compare?tools=${compareSlugs.join(',')}`, { replace: true });
    } else {
      navigate('/compare', { replace: true });
    }
  }, [compareSlugs, navigate]);

  const tools = compareSlugs
    .map(s => allTools.find(t => t.slug === s))
    .filter((t): t is Tool => Boolean(t));

  const hasEnough = tools.length >= 2;

  useSEO({
    title: hasEnough
      ? `${tools.map(t => t.name).join(' vs ')} — Compare AI Tools | ToolNova`
      : 'Compare AI Tools Side by Side | ToolNova',
    description: hasEnough
      ? `Compare ${tools.map(t => t.name).join(', ')} side by side. Features, pricing, pros & cons to help you choose.`
      : 'Compare AI tools side by side on ToolNova — features, pricing, pros & cons for every category.',
    keywords: hasEnough
      ? tools.map(t => t.name).join(' vs ') + ', compare AI tools'
      : 'compare AI tools, AI tool comparison',
    canonical: `${siteUrl}/compare`,
    ogTitle: hasEnough
      ? `${tools.map(t => t.name).join(' vs ')} — Compare AI Tools | ToolNova`
      : 'Compare AI Tools Side by Side | ToolNova',
    ogDescription: hasEnough
      ? `Compare ${tools.map(t => t.name).join(', ')} side by side. Features, pricing, pros & cons to help you choose.`
      : 'Compare AI tools side by side on ToolNova — features, pricing, pros & cons for every category.',
    ogType: 'website',
    ogUrl: `${siteUrl}/compare`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: hasEnough
        ? `${tools.map(t => t.name).join(' vs ')} — AI Tool Comparison`
        : 'Compare AI Tools Side by Side',
      description: hasEnough
        ? `Compare ${tools.map(t => t.name).join(', ')} side by side — features, pricing, pros & cons.`
        : 'Compare AI tools side by side on ToolNova. Features, pricing, pros & cons for every category.',
      url: `${siteUrl}/compare`,
      publisher: {
        '@type': 'Organization',
        name: 'ToolNova',
        url: siteUrl,
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/81ddcca4e455aa4e3e9404d7fe6eefde/e7fd262c64409f252189cfe0fbf7a092.png',
        },
      },
    },
  });

  const scrollToComparison = () => {
    comparisonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToPicker = () => {
    pickerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] pb-32">
      <Navbar />

      {/* Page header */}
      <div className="max-w-7xl mx-auto px-4 pt-8 pb-6">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">
              {hasEnough
                ? tools.map(t => t.name).join(' vs ')
                : 'Compare AI Tools'}
            </h1>
            <p className="text-gray-400 text-sm">
              {hasEnough
                ? `${count} tools selected — scroll down to see the full comparison`
                : 'Pick 2 or 3 tools below to compare them side by side'}
            </p>
          </div>

          {hasEnough && (
            <button
              onClick={scrollToComparison}
              className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white text-sm font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-teal-500/25 hover:shadow-teal-400/35 hover:scale-105 active:scale-95 shrink-0"
            >
              <i className="ri-bar-chart-grouped-line"></i>
              View Comparison
              <i className="ri-arrow-down-line text-xs"></i>
            </button>
          )}
        </div>

        {/* How it works — only shown when no tools selected */}
        {count === 0 && (
          <div className="mt-5 grid grid-cols-3 gap-4">
            {[
              { icon: 'ri-search-line', step: '1', title: 'Search & filter', desc: 'Find tools by name or category below' },
              { icon: 'ri-add-circle-line', step: '2', title: 'Add 2–3 tools', desc: 'Click the Compare button on each card' },
              { icon: 'ri-bar-chart-grouped-line', step: '3', title: 'See comparison', desc: 'Features, pricing, pros & cons side by side' },
            ].map(item => (
              <div key={item.step} className="flex items-center gap-3 bg-gray-900/40 border border-gray-800/50 rounded-xl p-4">
                <div className="w-9 h-9 shrink-0 flex items-center justify-center bg-teal-500/10 rounded-xl border border-teal-500/20">
                  <i className={`${item.icon} text-teal-400 text-base`}></i>
                </div>
                <div>
                  <p className="text-white text-xs font-bold">{item.title}</p>
                  <p className="text-gray-500 text-xs mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tool picker — always visible */}
      <div ref={pickerRef} className="max-w-7xl mx-auto px-4 pb-8">
        <ToolPicker onCompare={scrollToComparison} />
      </div>

      {/* Comparison section — only when 2+ tools */}
      {hasEnough && (
        <div
          ref={comparisonRef}
          className="max-w-7xl mx-auto px-4 pt-8 border-t border-gray-800/60"
        >
          {/* Section divider */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 flex items-center justify-center bg-teal-500/10 rounded-xl border border-teal-500/25 shrink-0">
              <i className="ri-bar-chart-grouped-line text-teal-400 text-sm"></i>
            </div>
            <h2 className="text-white font-bold text-lg">Comparison Results</h2>
            <span className="text-gray-600 text-sm">— {tools.map(t => t.name).join(' vs ')}</span>
            <div className="flex-1 h-px bg-gray-800/80"></div>
          </div>

          <ComparisonSection tools={tools} onBackToBrowser={scrollToPicker} />
        </div>
      )}
    </div>
  );
}
